/**
 * Screenplay keymap. Element flow (Enter/Tab/Shift-Tab) is data-driven from the
 * document's StyleDefs; only Enter/Tab/Backspace are intercepted so the Android
 * IME keeps full control of text input and composition.
 */
import { keymap } from "prosemirror-keymap";
import {
  chainCommands,
  deleteSelection,
  joinBackward,
  selectNodeBackward,
  toggleMark,
} from "prosemirror-commands";
import { undo, redo } from "prosemirror-history";
import type { Command, Plugin } from "prosemirror-state";
import type { StyleDef } from "../format/types";
import { screenplaySchema } from "./schema";
import { elementEnter, elementTab, elementShiftTab, setElementType } from "./commands";
import { elementShortcuts } from "./flow";

/** Backspace at the start of an empty/any paragraph merges with the previous. */
export const backspaceCommand = chainCommands(
  deleteSelection,
  joinBackward,
  selectNodeBackward,
);

export function buildKeymap(styles: Map<string, StyleDef>): Plugin {
  const m = screenplaySchema.marks;
  const bindings: Record<string, Command> = {
    Enter: elementEnter(styles),
    Tab: elementTab(styles),
    "Shift-Tab": elementShiftTab(styles),
    Backspace: backspaceCommand,
    "Mod-z": undo,
    "Mod-y": redo,
    "Shift-Mod-z": redo,
    "Mod-b": toggleMark(m.strong),
    "Mod-i": toggleMark(m.em),
    "Mod-u": toggleMark(m.underline),
  };

  // Fade In element shortcuts: Ctrl/Cmd + builtin_index (e.g. Ctrl+3 Character).
  for (const { key, style } of elementShortcuts(styles)) {
    bindings[key] = setElementType(styles, style);
  }

  return keymap(bindings);
}
